import React, { useState, useCallback, useEffect, useSyncExternalStore, useMemo } from 'react';
import { assetsOrgAssetsCreate } from '@goldcast/api/content';
import { ElementType } from '@goldcast/player-config/src/types/Element/Type';
import AudioLoopToggle from '@/Pages/Clip/ClipPlayer/ClipAssets/AudioOverlay/AudioLoopToggle';
import AudioFadeControls from '@/Pages/Clip/ClipPlayer/ClipAssets/AudioOverlay/AudioFadeControls';
import PercentageInput from '@/Pages/Clip/ClipPlayer/ClipAssets/AudioOverlay/PercentageInput';
import AudioOverlayLibraryModal from '@/Pages/Clip/ClipPlayer/ClipAssets/AudioOverlay/AudioOverlayLibraryModal';
import { useAudioOverlay } from '@/Pages/Clip/ClipPlayer/ClipAssets/AudioOverlay/useAudioOverlay';
import { MUSIC_LIBRARY, findMusicByUrl, MusicTrack, ASSET_PATH } from '@/Pages/Clip/ClipPlayer/ClipAssets/constants';
import {
  addToRecentlyUsed,
  getAudioDisplayName
} from '@/Pages/Clip/ClipPlayer/ClipAssets/AudioOverlay/audioOverlayLibraryUtils';
import { FeatureFlagKeys } from '@/services/featureFlag';
import featureFlagStore from '@/stores/featureFlagStore';
import { trackClipUpdate } from '@/stores/clip';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { useMultiAssetUploader } from '@/components/molecules/MultiAssetUploader/useMultiAssetUploader';
import { AUDIO_FORMATS, AUDIO_MAX_SIZE } from '@/components/molecules/MultiAssetUploader/constants';
import type { FileValidationResult, UploadError } from '@/components/molecules/MultiAssetUploader/types';
import { currentUser } from '@/stores/user';
import { useAppContext } from '@/context/AppContext/AppContext';
import { showErrorToast, showSuccessToast } from '@/libs/toast/toast';
import { getAudioDurationFromUrl } from '@/libs/getAudioDuration';
import RangeSlider from '@/components/atoms/RangeSlider';
import Icon from '@/components/atoms/Icon';

interface AudioOverlayControlsProps {
  element: {
    id: string;
    name: string;
    type: ElementType;
    details?: any;
    src?: string;
    [key: string]: any;
  };
  debouncedUpdate: (updates: any) => void;
}

const AudioOverlayControls: React.FC<AudioOverlayControlsProps> = ({ element, debouncedUpdate }) => {
  const { clipId, clipData, fps } = useClipsContext();
  const featureFlags = useSyncExternalStore(featureFlagStore.subscribe, featureFlagStore.getSnapshot);
  const isUseCLReplaceAudioEnabled = featureFlags[FeatureFlagKeys.Use_CL_Replace_Audio];
  const isUseCLAudioTransitionsEnabled = useMemo(
    () => featureFlags[FeatureFlagKeys.Use_CL_Audio_Transitions],
    [featureFlags]
  );
  const { logger } = useAppContext();

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { toggleAudioLoop, isAudioElementLooping } = useAudioOverlay();

  const organization = currentUser.getSnapshot()?.organization;
  const baseFilePath = useMemo(() => `${organization}/clip-asset`, [organization]);

  const calculateToFrame = useCallback(
    (newDuration: number, willLoop: boolean): number => {
      if (!willLoop) {
        return element.fromFrame + newDuration * fps;
      }
      return element.toFrame;
    },
    [element.fromFrame, element.toFrame, fps, clipData]
  );

  const [volume, setVolume] = useState(() => {
    if (element.type === ElementType.AUDIO && element.details?.volume?.[0]) {
      return element.details.volume[0].value;
    }
    return 100;
  });

  const isLooping = isAudioElementLooping(element.id);

  const getCurrentSelectedMusic = useCallback((): MusicTrack | null => {
    const currentUrl = element.details?.src || element.src;
    if (!currentUrl) {
      return null;
    }
    // Use filename-based matching to handle different CDN aliases
    const libraryTrack = findMusicByUrl(currentUrl);
    if (libraryTrack) {
      return libraryTrack;
    }
    // If not found in library, create custom upload track
    const filename = currentUrl.split('/').pop() || 'custom-audio';
    const cleanFilename = filename.split('?')[0];
    const customId = `custom-${cleanFilename.replace(/[^a-zA-Z0-9]/g, '-') || 'upload'}`;

    // Format display name
    let name = 'Custom Upload';
    if (element.name && element.name.trim()) {
      name = element.name;
    } else if (cleanFilename) {
      const nameWithoutExt = cleanFilename.replace(/\.[^/.]+$/, '');
      const formatted = nameWithoutExt.replace(/[-_]/g, ' ').trim();
      name =
        formatted
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' ') || 'Custom Upload';
    }

    return {
      id: customId,
      name,
      artist: '',
      description: 'Custom Upload',
      url: currentUrl,
      filename: cleanFilename,
      categories: []
    };
  }, [element]);

  const [selectedMusic, setSelectedMusic] = useState<MusicTrack | null>(getCurrentSelectedMusic());

  useEffect(() => {
    const currentMusic = getCurrentSelectedMusic();
    setSelectedMusic(currentMusic);
  }, [element.details?.src, element.src, getCurrentSelectedMusic]);

  // Check if there's any audio (library or custom)
  const hasAudio = useMemo(() => {
    return !!(element.details?.src || element.src);
  }, [element.details?.src, element.src]);

  // Get display name for audio (library track name or custom filename)
  const audioDisplayName = useMemo(() => {
    if (selectedMusic) {
      return selectedMusic.name;
    }
    return getAudioDisplayName({ url: element.details?.src || element.src, name: element.name });
  }, [selectedMusic, element.name, element.details?.src, element.src]);

  useEffect(() => {
    const handleClickOutside = () => {
      setIsDropdownOpen(false);
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isDropdownOpen]);

  const handleMusicChange = useCallback(
    async (musicId: string) => {
      const selectedOption = MUSIC_LIBRARY[musicId];
      if (selectedOption) {
        setSelectedMusic(selectedOption);
        setIsDropdownOpen(false);

        // Fetch duration from URL for library music
        let newDuration: number | undefined;
        try {
          newDuration = await getAudioDurationFromUrl(selectedOption.url);
        } catch (error) {
          logger.warn('Failed to fetch audio duration from URL:', error);
        }

        const newToFrame = newDuration ? calculateToFrame(newDuration, isLooping) : undefined;

        debouncedUpdate({
          name: selectedOption.name,
          details: {
            ...element.details,
            src: selectedOption.url,
            duration: newDuration,
            loop: isLooping
          },
          ...(newToFrame !== undefined && { toFrame: newToFrame })
        });
      }
    },
    [debouncedUpdate, element.details, isLooping, logger, calculateToFrame]
  );

  const handleLoopToggle = useCallback(async () => {
    await toggleAudioLoop(element.id);
  }, [toggleAudioLoop, element.id]);

  const handleVolumeChange = useCallback(
    (newVolume: number) => {
      setVolume(newVolume);

      if (element.type === ElementType.AUDIO && element.details?.volume) {
        // Clone the existing volume array and update the value in the first (or current) volume segment
        const updatedVolume = element.details.volume.map((volumeSegment: any, index: number) => {
          if (index === 0) {
            // For now, we're updating the first volume segment
            return {
              ...volumeSegment,
              value: newVolume
            };
          }
          return volumeSegment;
        });

        debouncedUpdate({
          details: {
            ...element.details,
            volume: updatedVolume,
            loop: isLooping
          }
        });
      }
    },
    [debouncedUpdate, element, isLooping]
  );

  // Upload file for replacement (doesn't add new entry to timeline)
  const handleUploadedAssetsForReplacement = useCallback(
    async (result: FileValidationResult) => {
      if (result.validFiles.length > 0) {
        const uploadedAsset = result.validFiles[0];
        const currentIsLooping = isAudioElementLooping(element.id);
        try {
          // Create asset entry in backend
          await assetsOrgAssetsCreate({
            body: [
              {
                name: uploadedAsset.filename,
                source_url: uploadedAsset.url,
                type: 'AUDIO'
              }
            ],
            queryParams: {}
          });

          // Get duration from uploaded asset or fetch from URL
          let newDuration = uploadedAsset.duration;
          if (!newDuration && uploadedAsset.url) {
            try {
              newDuration = await getAudioDurationFromUrl(uploadedAsset.url);
            } catch (error) {
              logger.warn('Failed to fetch audio duration from URL:', error);
            }
          }

          const newToFrame = newDuration ? calculateToFrame(newDuration, currentIsLooping) : undefined;

          // Update existing element with new audio source, preserve volume and loop, update duration
          debouncedUpdate({
            name: uploadedAsset.filename,
            details: {
              ...element.details,
              src: uploadedAsset.url,
              duration: newDuration,
              loop: currentIsLooping
            },
            ...(newToFrame !== undefined && { toFrame: newToFrame })
          });

          setSelectedMusic(null);

          // Track analytics for replacement
          trackClipUpdate(clipId, 'AUDIO_OVERLAY_REPLACE', {
            replacementSource: 'upload',
            selectedSongName: uploadedAsset.filename
          });

          // Close modal right before showing toast
          setIsModalOpen(false);
          showSuccessToast('Audio replaced successfully');
        } catch (error) {
          logger.error('Failed to replace audio:', error);
          showErrorToast('Failed to replace audio');
        }
      }
    },
    [element.id, element.details, debouncedUpdate, clipId, logger, isAudioElementLooping, calculateToFrame]
  );

  const handleUploadError = useCallback(
    (error: UploadError) => {
      logger.error(error);
      showErrorToast('message' in error ? error.message : 'Failed to upload audio');
    },
    [logger]
  );

  const { uploadFiles } = useMultiAssetUploader({
    baseFilePath,
    assetPath: ASSET_PATH,
    formats: AUDIO_FORMATS,
    maxSize: AUDIO_MAX_SIZE,
    handleUploadedAssets: handleUploadedAssetsForReplacement,
    onError: handleUploadError
  });

  const handleModalConfirm = useCallback(
    async (selection: { type: 'library'; musicId: string } | { type: 'upload'; file: File }) => {
      if (selection.type === 'library') {
        const selectedOption = MUSIC_LIBRARY[selection.musicId];
        if (selectedOption) {
          setSelectedMusic(selectedOption);
          setIsModalOpen(false);

          // Fetch duration from URL for library music
          let newDuration: number | undefined;
          try {
            newDuration = await getAudioDurationFromUrl(selectedOption.url);
          } catch (error) {
            logger.warn('Failed to fetch audio duration from URL:', error);
          }

          const newToFrame = newDuration ? calculateToFrame(newDuration, isLooping) : undefined;

          // Update element with new music source, preserve volume and loop, update duration
          debouncedUpdate({
            name: selectedOption.name,
            details: {
              ...element.details,
              src: selectedOption.url,
              duration: newDuration,
              loop: isLooping
            },
            ...(newToFrame !== undefined && { toFrame: newToFrame })
          });

          // Add to recently used
          addToRecentlyUsed(selection.musicId);

          // Track analytics
          trackClipUpdate(clipId, 'AUDIO_OVERLAY_REPLACE', {
            replacementSource: 'library',
            selectedSongId: selection.musicId,
            selectedSongName: selectedOption.name
          });
        }
      } else {
        // Handle file upload - replace existing audio instead of creating new entry
        try {
          await uploadFiles([selection.file]);
        } catch (error) {
          logger.error('Failed to upload file:', error);
          showErrorToast('Failed to upload audio');
        }
      }
    },
    [debouncedUpdate, element.details, isLooping, uploadFiles, clipId, logger, calculateToFrame]
  );

  return (
    <>
      {/* Music Selection - Show button (flag ON) or dropdown (flag OFF) for library music */}
      {hasAudio && (
        <div className="relative">
          {isUseCLReplaceAudioEnabled ? (
            <button
              type="button"
              data-testid="replace-music-button"
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                setIsModalOpen(true);
              }}
              className="flex w-full min-w-0 cursor-pointer items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-2 py-2.5 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-deep-orange-600 focus:ring-offset-1"
            >
              <div className="flex min-w-0 items-center gap-2">
                <Icon name="IconMusic" size={16} className="shrink-0 text-gray-600" />
                <div className="min-w-0 flex-1 text-left">
                  <div className="truncate text-sm font-medium text-gray-900">{audioDisplayName}</div>
                </div>
              </div>
              <span className="shrink-0 text-xs text-deep-orange-600">Replace</span>
            </button>
          ) : // Flag OFF: Show dropdown (original experience) - only for library music
          selectedMusic ? (
            <>
              <button
                type="button"
                onMouseDown={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsDropdownOpen(!isDropdownOpen);
                }}
                className="flex w-full min-w-0 cursor-pointer items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2.5 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-deep-orange-600 focus:ring-offset-1"
              >
                <div className="flex min-w-0 items-center gap-2">
                  <Icon name="IconMusic" size={16} className="shrink-0 text-gray-600" />
                  <div className="min-w-0 flex-1 text-left">
                    <div className="truncate text-sm font-medium text-gray-900">{selectedMusic.name}</div>
                  </div>
                </div>
                <Icon name="IconChevronDown" size={16} className="shrink-0 text-gray-500" />
              </button>

              {/* Dropdown Menu */}
              {isDropdownOpen && (
                <div className="absolute inset-x-0 top-full z-10 mt-1 max-h-48 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg">
                  {Object.entries(MUSIC_LIBRARY).map(([id, music]) => (
                    <button
                      key={id}
                      type="button"
                      onMouseDown={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleMusicChange(id);
                      }}
                      className={`w-full cursor-pointer px-3 py-2.5 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none ${
                        selectedMusic?.id === id ? 'bg-deep-orange-50' : ''
                      }`}
                    >
                      <div className="text-sm font-medium text-gray-900">{music.name}</div>
                      <div className="text-xs text-gray-500">{music.description}</div>
                    </button>
                  ))}
                </div>
              )}
            </>
          ) : null}
        </div>
      )}

      {/* Volume Control */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-gray-700">Volume</h4>
        <div className="flex items-center gap-3">
          {volume === 0 ? (
            <Icon name="IconVolumeOff" size={18} className="shrink-0 text-gray-500" />
          ) : (
            <Icon name="IconVolume" size={18} className="shrink-0 text-gray-600" />
          )}
          <div className="flex-1">
            <RangeSlider id="audio-volume" min={0} max={100} step={1} value={volume} onChange={handleVolumeChange} />
          </div>
          <PercentageInput value={volume} onChange={handleVolumeChange} data-testid="audio-volume-percentage" />
        </div>
      </div>
      {isUseCLAudioTransitionsEnabled && <AudioFadeControls element={element} debouncedUpdate={debouncedUpdate} />}
      <AudioLoopToggle isLooping={isLooping} onToggle={handleLoopToggle} />

      {isUseCLReplaceAudioEnabled && (
        <AudioOverlayLibraryModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onConfirm={handleModalConfirm}
          currentMusic={selectedMusic}
        />
      )}
    </>
  );
};

export default AudioOverlayControls;
